// EL CAMPO DE UN CÓDIGO DE BARRAS · U-02 fase b (DISEÑO §6.2).
//
// Tres cosas, y las tres mientras se escribe, no al guardar:
//
//   1. SE AUTOCORRIGE LO TECLEADO, y solo eso. Quita espacios (ningún
//      código los lleva: «7501 0553 6351 3» queda «7501055363513») y pone
//      en mayúsculas las letras de los tipos que el servidor guarda así. Los
//      guiones se quedan. Lo que llega del lector o de la cámara solo se
//      recorta (`limpiarCodigo`; auditoría U-02 b, H2).
//   2. DICE SI ESTÁ BIEN, con la misma regla que el servidor
//      (`estadoDelCodigo`, gemela de `codigos.validar`): verde, amarillo,
//      rojo o gris, en una línea debajo del campo. Quien lo usa apaga su
//      botón con `estado.motivo` a la vista.
//   3. SABE POR DÓNDE LLEGÓ: una ráfaga de teclas con Enter es el lector
//      (`useEscanerTeclado`, que la detecta aunque el foco no esté en el
//      campo), la cámara es la cámara, y escribir o pegar es teclado. Con
//      eso la ficha manda `origen` (ESCANEADO o TECLADO).
//
// Controlado: el padre guarda `valor`, `medio` y `tipo` y los recibe en
// `onCambio({ valor, medio, tipo })`. `Camara` es el componente de la
// cámara; se puede cambiar (las pruebas pasan uno que lee al instante).
import { useId, useState } from 'react';
import { AlertTriangle, Camera, CheckCircle2, Info, XCircle } from 'lucide-react';
import EscanerCamara from '../mobile/EscanerCamara';
import { useEscanerTeclado } from '../lib/useEscanerTeclado';
import { estadoDelCodigo, limpiarCodigo } from './codigos';

const COLOR = {
  verde: 'text-green-700',
  amarillo: 'text-amber-800',
  rojo: 'text-red-700',
  gris: 'text-gray-600',
};
const ICONO = { verde: CheckCircle2, amarillo: AlertTriangle, rojo: XCircle, gris: Info };
const DICHO_DEL_MEDIO = { LECTOR: 'Leído con el lector.', CAMARA: 'Leído con la cámara.' };

export default function CampoCodigo({
  valor, medio = null, tipo = null, onCambio,
  etiqueta = 'Código', ayuda = null, autoFocus = false,
  Camara = EscanerCamara, escucharLector = true,
}) {
  const id = useId();
  const [camara, setCamara] = useState(false);
  const estado = estadoDelCodigo(valor, tipo);
  const Icono = ICONO[estado.nivel];

  function cambiar(texto, nuevoMedio, nuevoTipo = tipo) {
    onCambio({ valor: limpiarCodigo(texto, nuevoTipo, nuevoMedio), medio: nuevoMedio, tipo: nuevoTipo });
  }

  // El lector teclea en la página entera: con el foco en el campo, sus
  // dígitos entran además por `onChange` como TECLADO, y al llegar el Enter
  // esto deja el código entero y el medio correcto. Con la cámara abierta
  // no escucha: ahí se lee con la cámara.
  useEscanerTeclado((leido, { medio: m } = {}) => cambiar(leido, m || 'LECTOR'),
    { activo: escucharLector && !camara });

  return (
    <div className="space-y-1 min-w-0">
      <label htmlFor={id} className="block text-sm font-semibold text-gray-700">{etiqueta}</label>
      <div className="flex flex-wrap gap-2">
        <input
          id={id}
          value={valor}
          onChange={(e) => cambiar(e.target.value, 'TECLADO')}
          autoFocus={autoFocus}
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          aria-invalid={estado.nivel === 'rojo'}
          aria-describedby={`${id}-estado`}
          className="entrada font-mono min-w-0 flex-1 min-h-11"
          placeholder="7501055363513"
        />
        {Camara && !camara && (
          <button type="button" onClick={() => setCamara(true)}
                  className="inline-flex items-center justify-center gap-1.5 min-h-11 px-3 rounded-lg border border-gray-300 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50">
            <Camera className="h-4 w-4" aria-hidden="true" /> Leer con la cámara
          </button>
        )}
      </div>
      {camara && (
        <Camara
          onCodigo={(t) => { setCamara(false); cambiar(t, 'CAMARA'); }}
          onCerrar={() => setCamara(false)}
        />
      )}
      {ayuda}
      <p id={`${id}-estado`} role="status" data-nivel={estado.nivel}
         className={`flex items-start gap-1.5 text-sm break-words ${COLOR[estado.nivel] || 'text-gray-600'}`}>
        {Icono && <Icono className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />}
        <span className="min-w-0">{estado.texto}</span>
      </p>
      {estado.aMedias && (
        <button type="button" onClick={() => cambiar(valor, medio, 'INTERNO')}
                className="min-h-11 text-sm font-semibold text-[var(--color-marca)] underline text-left">
          Es un código interno: añadirlo así
        </button>
      )}
      {tipo === 'INTERNO' && (
        <p className="text-sm text-gray-600">
          Marcado como código interno.{' '}
          <button type="button" onClick={() => cambiar(valor, medio, null)}
                  className="min-h-11 font-semibold text-[var(--color-marca)] underline">
            Comprobarlo como código de barras
          </button>
        </p>
      )}
      {valor && DICHO_DEL_MEDIO[medio] && (
        <p className="text-xs text-gray-500" data-medio={medio}>{DICHO_DEL_MEDIO[medio]}</p>
      )}
    </div>
  );
}
